import { EqualIcon } from "lucide-react";
import { useCallback, useMemo } from "react";
import {
  T,
  RecordProps,
  Editor,
  TLShapeUtilCanBindOpts,
  useValue,
  useEditor,
  TLAsset,
  TLBinding,
  TLShape,
} from "tldraw";

import {
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
  BlockBody,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasInput,
  CanvasSimpleSelect,
  OperatorSelect,
} from "@/components/canvas-editor/components/Inputs";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  choicesFor,
  referencedParameter,
  unreachableReason,
  valueSpaceFor,
} from "@/components/canvas-editor/param-compare-typing";
import {
  BindableUtil,
  createMigrations,
  dropTargetId,
} from "@/components/canvas-editor/shapes/bindable";
import {
  Dimensions,
  ParamCompareProps,
  ParamCompareShape,
  paramCompareType,
  pinnableShapes,
  metadataType,
  comparisonOperators,
  ComparisonOperator,
  isCustomNodeRootShape,
} from "@/components/canvas-editor/shapes/shapes";
import { ToolStateNode } from "@/components/canvas-editor/utils";
import { cn } from "@/lib/utils";

export function isParamCompareShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is ParamCompareShape {
  return shape.type === paramCompareType;
}

export class ParamCompareTool extends ToolStateNode {
  static override id = paramCompareType;

  static override uiToolDef = (editor: Editor) => ({
    id: paramCompareType,
    icon: EqualIcon,
    label: "Param Compare",
    defaultColor: ParamCompareShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(paramCompareType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={ParamCompare}
        util={ParamCompareShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: paramCompareType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class ParamCompareShapeUtil extends BindableUtil<ParamCompareShape> {
  static override migrations = createMigrations(paramCompareType);
  static override type = paramCompareType;

  static override docs = {
    defaultColor: "blue" as const,
    title: "Param Compare",
    description: "Use this node to compare a parameter to a value.",
  };

  override getType() {
    return paramCompareType;
  }

  static override props: RecordProps<ParamCompareShape> = {
    w: T.number,
    minW: T.number,
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    metadata: metadataType,
    compare: T.string,
    var: T.string,
    operator: T.literalEnum(...comparisonOperators),
  };

  override getDefaultProps() {
    return {
      w: 320,
      minW: 320,
      h: 101,
      compare: "",
      var: "",
      operator: "==" as const,
    };
  }

  override canBind({
    toShapeType,
    bindingType,
  }: TLShapeUtilCanBindOpts<ParamCompareShape>) {
    if (bindingType === "arrow") {
      return true;
    }

    return pinnableShapes.includes(toShapeType) && bindingType === "layout";
  }

  override component(shape: ParamCompareShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <ParamCompare
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function ParamCompare({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: ParamCompareShape;
  className?: string;
  updateShape: (
    shape: ParamCompareShape,
    props: Partial<ParamCompareProps>,
  ) => void;
}) {
  const variant =
    shape.props.compare === "off" || shape.props.compare === "false"
      ? "red"
      : "green";
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  const id = useValue(
    "param-compare-atom",
    () => {
      return dropTargetId.get();
    },
    [dropTargetId],
  );

  // The parameters the node being edited declares. Read off the canvas rather
  // than passed in, because the declaration lives on the custom-node root shape
  // and this node may sit anywhere beneath it. Undefined on a feed canvas,
  // where there is no root and so nothing to narrow against.
  const editor = useEditor();
  const declaredParameters = useValue(
    "custom-node-declared-parameters",
    () =>
      editor.getCurrentPageShapes().find(isCustomNodeRootShape)?.props
        .customNodeParameters,
    [editor],
  );

  // A toggle substitutes a JSON boolean and a select substitutes a string, so
  // comparing either against the other kind of literal is false for every post
  // -- silently, because the condition is otherwise perfectly well formed. When
  // the parameter's value space is known, offer that space instead of a text
  // box so the impossible comparison cannot be typed in the first place.
  const valueSpace = useMemo(
    () =>
      valueSpaceFor(referencedParameter(shape.props.var, declaredParameters)),
    [shape.props.var, declaredParameters],
  );
  // Judged, never corrected. A node already carrying one of these is doing
  // something today, and quietly rewriting it would change what someone's feed
  // matches without them asking.
  const impossible = unreachableReason(
    valueSpace,
    shape.props.compare,
    shape.props.operator,
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        isBound={isBound}
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        className={cn(
          className,
          id && shape.id === id && "rounded-lg shadow-[0_0_0_4px_#0588F0]",
        )}
        width={Math.max(shape.props.minW || 0, shape.props.w)}
        height={shape.props.h}
        variant={shape.props.metadata?.color || variant}
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={{
            title: "Param Compare",
            description:
              "Use this node to check what one of your node's parameters is set to. Pick the parameter on the left and the value to check for on the right; for a toggle or a select, the value box offers exactly the values that parameter can hold.",
            variant: variant,
          }}
        />
        <BlockBody className="p-4 flex flex-col gap-2">
          <div className="flex gap-2">
            <CanvasInput
              aria-label="Custom Node Parameter"
              value={shape.props.var}
              placeholder="Parameter"
              className="flex-1"
              onValueChange={(v) => updateShape(shape, { var: v })}
              onInput={(e) => {
                if (!e.currentTarget.value.match(/^\$/)) {
                  e.currentTarget.setCustomValidity(
                    "You must use a custom node parameter. Use $ to reference a one.",
                  );
                  e.currentTarget.reportValidity();
                } else {
                  e.currentTarget.setCustomValidity("");
                  e.currentTarget.reportValidity();
                }
              }}
            />
            <OperatorSelect
              value={shape.props.operator}
              onValueChange={(v) =>
                updateShape(shape, { operator: v as ComparisonOperator })
              }
            />
            {valueSpace.kind === "free" ? (
              <CanvasInput
                aria-label="Comparison Value"
                value={shape.props.compare}
                placeholder="Value"
                className="flex-1"
                onValueChange={(v) => updateShape(shape, { compare: v })}
                onBlur={() => {
                  if (
                    shape.props.compare.toLowerCase() === "true" &&
                    shape.props.compare !== "true"
                  ) {
                    updateShape(shape, { compare: "true" });
                  } else if (
                    shape.props.compare.toLowerCase() === "false" &&
                    shape.props.compare !== "false"
                  ) {
                    updateShape(shape, { compare: "false" });
                  }
                }}
              />
            ) : (
              // `CanvasSimpleSelect` prepends a stored value that is not among
              // the choices, so an existing comparison stays selected and visible
              // instead of being silently reassigned to the first option.
              <CanvasSimpleSelect
                aria-label="Comparison Value"
                value={shape.props.compare}
                className="flex-1"
                options={choicesFor(valueSpace)}
                onValueChange={(v) => updateShape(shape, { compare: v })}
              />
            )}
          </div>
          {impossible && (
            <div className="text-xs text-red-11 dark:text-reddark-11">
              {impossible}
            </div>
          )}
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
